/** @deprecated Compatibility hook for imperative callers. Use useApiQuery for reads and useApiMutation for writes. */
import { ApiDefinition, ApiRequest, ApiResponse, callApi } from "@/api/call-api";
import { ApiLoadingSetStateContext } from "@/context/common/general/api-loading-context";
import { useCallback, useContext, useRef, useState } from "react";

type ApiResponseData<T> = T extends { data?: infer TData } ? TData : undefined;

type ApiError = Error & { code?: string; status?: string | number };

type UseApiParams<TApi, TRequest, TResponse> = {
  api: TApi;
  request?: TRequest;
  onSuccess?: (response: TResponse) => void;
  onError?: (error: ApiError) => void;
  disableLoader?: boolean;
  timeoutMs?: number;
};

type UseApiResult<TResponse, TRequest, TData> = {
  call: (value?: TRequest) => Promise<TResponse>;
  loading: boolean;
  error: ApiError | null;
  response: TResponse | null;
  data: TData | null;
};

/**
 * @deprecated Use useApiQuery for reads or useApiMutation for writes.
 * Preserved for existing imperative callers; this hook has no shared cache.
 * A hook to call a cloud function using io-ts for type inference
 * @example
 * const {call, loading, error, data} = useApi<typeof Api.Tags.CreateTags>({
 *      api: Api.Tags.CreateTags,
 *      request: {
 *          tags: ["tag1", "tag2"],
 *          tag_type: TagTypes.Content,
 *      },
 * });
 */
function useApi<ApiFunction extends ApiDefinition>({
  api,
  request = {} as ApiRequest<ApiFunction>,
  onSuccess,
  onError,
  disableLoader,
  timeoutMs,
}: UseApiParams<ApiFunction, ApiRequest<ApiFunction>, ApiResponse<ApiFunction>>): UseApiResult<
  ApiResponse<ApiFunction>,
  ApiRequest<ApiFunction>,
  ApiResponseData<ApiResponse<ApiFunction>>
> {
  type Request = ApiRequest<ApiFunction>;
  type Response = ApiResponse<ApiFunction>;
  type ResponseData = ApiResponseData<ApiResponse<ApiFunction>>;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [response, setResponse] = useState<Response | null>(null);
  const [data, setData] = useState<ResponseData | null>(null);

  const pendingCalls = useRef(0);

  const setLoadingState = useContext(ApiLoadingSetStateContext);

  const timeoutRef = useRef(timeoutMs);
  timeoutRef.current = timeoutMs;
  const apiRef = useRef(api);
  apiRef.current = api;
  const requestRef = useRef(request);
  requestRef.current = request;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const disableLoaderRef = useRef(disableLoader);
  disableLoaderRef.current = disableLoader;
  const setLoadingStateRef = useRef(setLoadingState);
  setLoadingStateRef.current = setLoadingState;

  const call = useCallback(async (requestOverride?: Request): Promise<Response> => {
    const apiName = apiRef.current.Name;

    pendingCalls.current += 1;
    setLoading(true);
    const showLoader = !disableLoaderRef.current;
    const updateLoading = setLoadingStateRef.current;
    setError(null);

    if (showLoader) {
      updateLoading(prevState => ({
        ...prevState,
        [apiName]: (prevState[apiName] ?? 0) + 1,
      }));
    }

    try {
      const responseData = await callApi(apiRef.current, requestOverride ?? requestRef.current, { timeoutMs: timeoutRef.current });
      setResponse(responseData);
      setData((responseData as { data?: ResponseData } | null)?.data ?? null);
      onSuccessRef.current?.(responseData);
      return responseData;
    } catch (err: unknown) {
      const apiError: ApiError = err instanceof Error ? err : new Error(String(err));
      setData(null);

      setError(apiError);
      onErrorRef.current?.(apiError);
      throw err;
    } finally {
      pendingCalls.current -= 1;
      setLoading(pendingCalls.current > 0);
      if (showLoader) {
        updateLoading(prevState => ({
          ...prevState,
          [apiName]: Math.max(0, (prevState[apiName] ?? 0) - 1),
        }));
      }
    }
  }, []);

  return { call, loading, error, response, data };
}

export default useApi;
